import type { RouteObject } from "react-router-dom";
import { lazy } from "react";
import { addSlashes, withCompanyPrefix } from "../route.utils";
import { MODULE_SETTINGS_PATHS } from "./module-settings.paths";
import { Spinner } from "@/components/ui/v2/spinner";
import ManagePerformance, { TeamMembersPage } from "@/pages/(home)/(company)/performance/manage";
import { PerformanceCyclesList } from "@/pages/(home)/(company)/performance/_components/performance-cycles-list";
import { PerformanceConfigForm } from "@/pages/(home)/(company)/performance/manage/configuration/configuration-form";
import { PerformanceConfiguration } from "@/pages/(home)/(company)/performance/manage/configuration";
import { PerformanceEvaluatorsList } from "@/pages/(home)/(company)/performance/_components/performance-evaluators-list";
import { PerformanceIndividualReview } from "@/components/performance/individual-review";
import { store } from "@/store";
import { companyTeamsApi } from "@/services/(company)/api.teams";
import { performanceApi } from "@/services/(company)/api.performance";
import { PerformanceMetricsForm } from "@/pages/(home)/(company)/performance/settings/metrics/_components/metrics-form";

// Lazy so the route file doesn't eagerly pull the tabs page (which imports
// nav-tabs.config) during config initialization — avoids a circular-import TDZ.
const CompanyPerformance = lazy(
  () => import("@/pages/(home)/(company)/performance"),
);
const CompanyPerformanceListPage = lazy(
  () => import("@/pages/(home)/(company)/performance/list"),
);
const CompanyPerformanceSettingsPage = lazy(() =>
  import("@/pages/(home)/(company)/performance/settings").then((m) => ({
    default: m.CompanyPerformanceSettings,
  })),
);

// ─── Path Segments ────────────────────────────────────────────────────────────
export const PERFORMANCE_ID_KEY = "id";
export const PERFORMANCE_CONFIG_ID = "configId";
export const PERFORMANCE_MEMBER_ID_KEY = "memberId";
export const PERFORMANCE_CYCLE_ID_KEY = "cycleId";
export const PERFORMANCE_REVIEW_ID_KEY = "reviewId";

export enum COMPANY_PERFORMANCE_PATH_SEGMENTS {
  ROOT = "performance",
  DETAILS = `:${PERFORMANCE_ID_KEY}`,
  CONFIG_DETILS = `:${PERFORMANCE_CONFIG_ID}`,
  PERFORMANCE_CYCLE = "performance-cycle",
  PERFORMANCE_CYCLE_DETAILS = `:${PERFORMANCE_MEMBER_ID_KEY}`,
  PERFORMANCE_CONFIGURATION = "performance-configuration",
  PERFORMANCE_EVALUATORS = "evaluators",
  PERFORMANCE_EVALUATORS_DETAILS = `:${PERFORMANCE_CYCLE_ID_KEY}`,
  PERFORMANCE_REVIEW = "review",
  PERFORMANCE_REVIEW_DETAILS = `:${PERFORMANCE_REVIEW_ID_KEY}`,
  CREATE = "create",
  EDIT = "edit",
  // Module settings (moved out of Company Settings)
  // Literal, not `MODULE_SETTINGS_SEGMENT`: `isolatedModules` forbids enum
  // members initialized from an imported const. Keep both in sync.
  SETTINGS = "settings",
  METRICS = "metrics",
}

// ─── Route Definition ─────────────────────────────────────────────────────────
export const companyPerformancetRoutes: RouteObject = {
  path: COMPANY_PERFORMANCE_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Performance" },
  HydrateFallback: Spinner,
  // No Component here — CompanyPerformance is the tabs layout, detail routes render standalone
  children: [
    // ── Page-level tabs layout (single "Teams" tab at the module root) ───────
    {
      Component: CompanyPerformance,
      handle: { flushContent: true },
      children: [{ index: true, Component: CompanyPerformanceListPage }],
    },

    // ── Module settings — a SIBLING of the tabs layout, never a child of it:
    // nesting it inside would render it under a tab bar with no matching tab.
    // Static "settings" also outranks the dynamic siblings in React Router's
    // route ranking, so there is no collision. ──────────────────────────────
    {
      path: COMPANY_PERFORMANCE_PATH_SEGMENTS.SETTINGS,
      handle: { breadcrumb: "Settings", title: "Performance Settings" },
      children: [
        { index: true, Component: CompanyPerformanceSettingsPage },
        // Metric form screens — they used to sit under
        // /company/settings/performance-settings/{create,:id/edit}.
        {
          path: `${COMPANY_PERFORMANCE_PATH_SEGMENTS.METRICS}/${COMPANY_PERFORMANCE_PATH_SEGMENTS.CREATE}`,
          handle: {
            breadcrumb: "Add Performance Metrics",
            title: "Add New Performance Metrics",
          },
          Component: PerformanceMetricsForm,
        },
        {
          path: `${COMPANY_PERFORMANCE_PATH_SEGMENTS.METRICS}/${COMPANY_PERFORMANCE_PATH_SEGMENTS.DETAILS}/${COMPANY_PERFORMANCE_PATH_SEGMENTS.EDIT}`,
          handle: { breadcrumb: "Edit" },
          Component: PerformanceMetricsForm,
        },
      ],
    },

    // ── Team detail routes (no stats, no layout) ─────────────────────────────
    {
      path: COMPANY_PERFORMANCE_PATH_SEGMENTS.DETAILS,
      loader: async ({ params }) => {
        const teamId = Number(params[PERFORMANCE_ID_KEY]);
        const result = await store.dispatch(companyTeamsApi.endpoints.companyGetTeamDetails.initiate({ teamId }));
        const name = result.data?.data?.name ?? `Team #${teamId}`;
        return { breadcrumbLabel: name };
      },
      handle: {
        breadcrumb: (data: { breadcrumbLabel: string }) => data?.breadcrumbLabel ?? "Team",
        title: "Performance",
      },
      children: [
        // ── NavLink tabs layout (covers only the two tab index pages) ────────
        {
          Component: ManagePerformance,
          children: [
            { index: true, Component: TeamMembersPage },
            {
              path: COMPANY_PERFORMANCE_PATH_SEGMENTS.PERFORMANCE_CONFIGURATION,
              handle: { breadcrumb: "Configuration" },
              Component: PerformanceConfiguration,
            },
          ],
        },

        // ── Outside tabs layout ───────────────────────────────────────────────
        {
          path: COMPANY_PERFORMANCE_PATH_SEGMENTS.PERFORMANCE_CONFIGURATION,
          handle: { breadcrumb: "Configuration" },
          children: [
            {
              path: COMPANY_PERFORMANCE_PATH_SEGMENTS.CONFIG_DETILS,
              loader: async ({ params }) => {
                const configId = params[PERFORMANCE_CONFIG_ID];
                if (!configId) return { breadcrumbLabel: `Configuration #${configId}` };
                const result = await store.dispatch(
                  performanceApi.endpoints.getPerformanceConfigurationById.initiate({ id: configId }),
                );
                const name = result.data?.data?.name ?? `Configuration #${configId}`;
                return { breadcrumbLabel: name };
              },
              handle: {
                breadcrumb: (data: { breadcrumbLabel: string }) => data?.breadcrumbLabel ?? "Configuration",
                title: "Performance",
              },
              children: [
                {
                  path: COMPANY_PERFORMANCE_PATH_SEGMENTS.EDIT,
                  handle: { breadcrumb: "Edit" },
                  Component: PerformanceConfigForm,
                },
              ],
            },
            {
              path: COMPANY_PERFORMANCE_PATH_SEGMENTS.CREATE,
              handle: { breadcrumb: "Configure" },
              Component: PerformanceConfigForm,
            },
          ],
        },
        {
          path: `${COMPANY_PERFORMANCE_PATH_SEGMENTS.PERFORMANCE_CYCLE}/${COMPANY_PERFORMANCE_PATH_SEGMENTS.PERFORMANCE_CYCLE_DETAILS}`,
          handle: { breadcrumb: "Performance Cycle" },
          Component: PerformanceCyclesList,
        },
        {
          path: `${COMPANY_PERFORMANCE_PATH_SEGMENTS.PERFORMANCE_EVALUATORS}/${COMPANY_PERFORMANCE_PATH_SEGMENTS.PERFORMANCE_EVALUATORS_DETAILS}`,
          handle: { breadcrumb: "Evaluators" },
          Component: PerformanceEvaluatorsList,
        },
        {
          path: `${COMPANY_PERFORMANCE_PATH_SEGMENTS.PERFORMANCE_REVIEW}/${COMPANY_PERFORMANCE_PATH_SEGMENTS.PERFORMANCE_REVIEW_DETAILS}`,
          handle: { breadcrumb: "Review" },
          Component: PerformanceIndividualReview,
        },
      ],
    },
  ],
};

// ─── Resolved Paths ───────────────────────────────────────────────────────────
export const COMPANY_PERFORMANCE_PATHS = Object.freeze({
  ROOT: withCompanyPrefix(addSlashes(COMPANY_PERFORMANCE_PATH_SEGMENTS.ROOT)),

  // ── Module settings (resolved in module-settings.paths.ts) ────────────────
  SETTINGS: MODULE_SETTINGS_PATHS.PERFORMANCE.ROOT,
  SETTINGS_VTAB_METRICS: MODULE_SETTINGS_PATHS.PERFORMANCE.VTAB_METRICS,
  SETTINGS_METRICS_CREATE: MODULE_SETTINGS_PATHS.PERFORMANCE.METRICS_CREATE,
  SETTINGS_METRICS_EDIT: MODULE_SETTINGS_PATHS.PERFORMANCE.METRICS_EDIT,

  MANAGE: (teamId: string | number) =>
    withCompanyPrefix(
      addSlashes(COMPANY_PERFORMANCE_PATH_SEGMENTS.ROOT, String(teamId)),
    ),
  PERFORMANCE_CYCLE: (teamId: string | number, memberId: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_PERFORMANCE_PATH_SEGMENTS.ROOT,
        String(teamId),
        COMPANY_PERFORMANCE_PATH_SEGMENTS.PERFORMANCE_CYCLE,
        String(memberId),
      ),
    ),
  PERFORMANCE_CONFIGURATION_CREATE: (teamId: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_PERFORMANCE_PATH_SEGMENTS.ROOT,
        String(teamId),
        COMPANY_PERFORMANCE_PATH_SEGMENTS.PERFORMANCE_CONFIGURATION,
        COMPANY_PERFORMANCE_PATH_SEGMENTS.CREATE,
      ),
    ),
  PERFORMANCE_CONFIGURATION_EDIT: (teamId: string | number, configId: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_PERFORMANCE_PATH_SEGMENTS.ROOT,
        String(teamId),
        COMPANY_PERFORMANCE_PATH_SEGMENTS.PERFORMANCE_CONFIGURATION,
        String(configId),
        COMPANY_PERFORMANCE_PATH_SEGMENTS.EDIT,
      ),
    ),
  PERFORMANCE_EVALUATORS: (teamId: string | number, cycleId: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_PERFORMANCE_PATH_SEGMENTS.ROOT,
        String(teamId),
        COMPANY_PERFORMANCE_PATH_SEGMENTS.PERFORMANCE_EVALUATORS,
        String(cycleId),
      ),
    ),
  PERFORMANCE_REVIEW: (teamId: string | number, reviewId: string | number) =>
    withCompanyPrefix(
      addSlashes(
        COMPANY_PERFORMANCE_PATH_SEGMENTS.ROOT,
        String(teamId),
        COMPANY_PERFORMANCE_PATH_SEGMENTS.PERFORMANCE_REVIEW,
        String(reviewId),
      ),
    ),
});
